import React, { useState, useEffect } from "react";
import { Card, Button, Spinner, Form, Modal } from "react-bootstrap";
import QuickQuizService from "../../_services/QuickQuizService";

interface MarksModerationPanelProps {
  testId: number;
  onBack: () => void;
  onViewTest?: (testId: number) => void;
}

const MarksModerationPanel: React.FC<MarksModerationPanelProps> = ({ testId, onBack, onViewTest }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Modify marks modal
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [modifyStudent, setModifyStudent] = useState<any>(null);
  const [modifyMarks, setModifyMarks] = useState<Record<number, string>>({});
  const [modifyRemarks, setModifyRemarks] = useState("");
  const [modifyTypeId, setModifyTypeId] = useState("");
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      const result = await QuickQuizService.getMarksModeration(testId);
      setData(result);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [testId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openModifyMarks = (student: any) => {
    setModifyStudent(student);
    const marks: Record<number, string> = {};
    for (const sec of (data?.sections || [])) {
      const sm = student.sectionMarks[sec.sectionId];
      marks[sec.sectionId] = sm?.marks != null ? String(sm.marks) : "0";
    }
    setModifyMarks(marks);
    setModifyRemarks("");
    setModifyTypeId("");
    setShowModifyModal(true);
  };

  const handleSaveModify = async () => {
    if (!modifyStudent) return;
    if (data?.modificationTypes?.length > 0 && !modifyTypeId) {
      // eslint-disable-next-line no-alert
      window.alert("Please select a Modification Type");
      return;
    }
    setSaving(true);
    try {
      const updates: Array<{ stsId: number; value: number; remarks?: string; modificationTypeId?: number }> = [];
      for (const sec of (data?.sections || [])) {
        const sm = modifyStudent.sectionMarks[sec.sectionId];
        if (sm?.stsId) {
          updates.push({
            stsId: sm.stsId,
            value: Number(modifyMarks[sec.sectionId] || 0),
            remarks: modifyRemarks || undefined,
            modificationTypeId: modifyTypeId ? Number(modifyTypeId) : undefined,
          });
        }
      }
      if (updates.length > 0) {
        await QuickQuizService.moderateMarks(testId, updates);
      }
      setShowModifyModal(false);
      // eslint-disable-next-line no-alert
      window.alert("Marks saved successfully. Grades and ranks recalculated.");
      setLoading(true);
      await loadData();
    } catch (err: any) {
      // eslint-disable-next-line no-alert
      window.alert(err?.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const calcTotal = (): number => {
    return Object.values(modifyMarks).reduce((sum, v) => sum + (Number(v) || 0), 0);
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner role="status" animation="border" />
        <p className="text-muted mt-2">Loading section-wise marks...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <Card.Body className="text-center py-4">
          <p className="text-danger">{error || "Failed to load data"}</p>
          <Button variant="secondary" size="sm" onClick={onBack}>Back</Button>
        </Card.Body>
      </Card>
    );
  }

  const { test, sections, students, modificationTypes } = data;

  // Filter
  let filtered = students || [];
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter((s: any) =>
      (s.studentName || "").toLowerCase().includes(q) ||
      (s.studentCode || "").toLowerCase().includes(q) ||
      String(s.enquiryId).includes(q),
    );
  }

  return (
    <>
      {/* Header */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center" style={{ padding: "8px 12px" }}>
          <span style={{ fontSize: "0.9rem" }}>
            <strong>Marks Moderation</strong>
            <span style={{ margin: "0 8px", color: "var(--text-muted)" }}>
              {test.name} (ID: {test.testId}) | Total Marks: {test.totalMarks}
            </span>
          </span>
          <div className="d-flex gap-2">
            <button className="btn btn-sm" onClick={() => onViewTest ? onViewTest(testId) : onBack()} style={{ fontSize: "0.8rem", padding: "5px 12px", backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", borderRadius: "4px", margin: "2px" }}>
              View Test
            </button>
            <button className="btn btn-sm" onClick={() => { setLoading(true); loadData(); }} style={{ fontSize: "0.8rem", padding: "5px 12px", backgroundColor: "var(--surface-card)", color: "var(--brand-primary)", border: "1px solid var(--brand-primary)", borderRadius: "4px", margin: "2px" }}>
              Refresh
            </button>
            <button className="btn btn-sm" onClick={onBack} style={{ fontSize: "0.8rem", padding: "5px 12px", backgroundColor: "var(--surface-card)", color: "var(--text-muted)", border: "1px solid #6c757d", borderRadius: "4px", margin: "2px" }}>
              Back
            </button>
          </div>
        </Card.Header>
      </Card>

      {/* Search */}
      <Card className="mb-3">
        <Card.Body style={{ padding: "8px 12px" }}>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Form.Control
              size="sm"
              placeholder="Search by name, code, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ maxWidth: "300px" }}
            />
            <span className="text-muted" style={{ fontSize: "0.8rem" }}>
              {filtered.length} of {(students || []).length} students
            </span>
          </div>
        </Card.Body>
      </Card>

      {/* Section-wise Marks Table */}
      <Card>
        <Card.Body style={{ padding: 0 }}>
          <div style={{ overflowX: "auto" }}>
            <table className="table table-sm table-bordered table-hover mb-0" style={{ fontSize: "0.78rem" }}>
              <thead className="table-light">
                <tr>
                  <th style={{ paddingLeft: "10px", whiteSpace: "nowrap", position: "sticky", left: 0, backgroundColor: "var(--surface-page)", zIndex: 1 }}>#</th>
                  <th style={{ minWidth: "150px", position: "sticky", left: "30px", backgroundColor: "var(--surface-page)", zIndex: 1 }}>Student</th>
                  <th style={{ whiteSpace: "nowrap" }}>Code</th>
                  {sections.map((sec: any) => (
                    <th key={sec.sectionId} className="text-center" style={{ whiteSpace: "nowrap" }}>
                      {sec.name}
                      <div style={{ fontSize: "0.65rem", color: "var(--text-muted)", fontWeight: 400 }}>({sec.totalMarks})</div>
                    </th>
                  ))}
                  <th className="text-center" style={{ whiteSpace: "nowrap", fontWeight: 700 }}>
                    Total
                    <div style={{ fontSize: "0.65rem", color: "var(--text-muted)", fontWeight: 400 }}>({test.totalMarks})</div>
                  </th>
                  <th className="text-center">Rank</th>
                  <th className="text-center">%</th>
                  <th className="text-center">Grade</th>
                  <th className="text-center">Remarks</th>
                  <th className="text-center" style={{ minWidth: "80px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7 + sections.length} className="text-center text-muted py-4">
                      {(students || []).length === 0 ? "No students found." : "No students match the search."}
                    </td>
                  </tr>
                ) : (
                  filtered.map((s: any, i: number) => (
                    <tr key={s.enrollmentId}>
                      <td style={{ paddingLeft: "10px", position: "sticky", left: 0, backgroundColor: "var(--surface-card)", zIndex: 1 }}>{i + 1}</td>
                      <td style={{ position: "sticky", left: "30px", backgroundColor: "var(--surface-card)", zIndex: 1 }}>
                        <strong>{s.studentName}</strong>
                      </td>
                      <td>{s.studentCode || "-"}</td>
                      {sections.map((sec: any) => {
                        const sm = s.sectionMarks[sec.sectionId];
                        return (
                          <td key={sec.sectionId} className="text-center">
                            {sm?.marks != null ? sm.marks : "-"}
                          </td>
                        );
                      })}
                      <td className="text-center" style={{ fontWeight: 600 }}>
                        {s.rootSts?.marks != null ? s.rootSts.marks : "-"}
                      </td>
                      <td className="text-center">{s.rootSts?.batchRank || s.rootSts?.testRank || "-"}</td>
                      <td className="text-center">{s.rootSts?.percentage != null ? `${Number(s.rootSts.percentage).toFixed(1)}%` : "-"}</td>
                      <td className="text-center">{s.rootSts?.grade || "-"}</td>
                      <td className="text-center" style={{ fontSize: "0.72rem" }}>{s.rootSts?.remarks || "-"}</td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          style={{ fontSize: "0.7rem", padding: "2px 8px", backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)" }}
                          onClick={() => openModifyMarks(s)}
                        >
                          Modify
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>

      {/* Modify Marks Modal */}
      <Modal show={showModifyModal} onHide={() => !saving && setShowModifyModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: "#2196F3", color: "var(--text-on-brand)", padding: "10px 16px" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Modify Marks — {modifyStudent?.studentName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: "0.85rem", marginBottom: "12px" }}>
            <strong>Student:</strong> {modifyStudent?.studentName} ({modifyStudent?.studentCode})
          </div>

          {sections.map((sec: any) => (
            <Form.Group key={sec.sectionId} className="mb-2">
              <div className="d-flex align-items-center justify-content-between">
                <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: 0 }}>
                  {sec.name} <span className="text-muted" style={{ fontWeight: 400 }}>(Max: {sec.totalMarks})</span>
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  max={sec.totalMarks}
                  step="0.5"
                  value={modifyMarks[sec.sectionId] || "0"}
                  onChange={(e) => setModifyMarks({ ...modifyMarks, [sec.sectionId]: e.target.value })}
                  style={{ width: "100px", textAlign: "center" }}
                  disabled={saving}
                />
              </div>
            </Form.Group>
          ))}

          <div className="d-flex justify-content-between mt-3 mb-3" style={{ fontSize: "0.9rem", fontWeight: 700 }}>
            <span>Total</span>
            <span>{calcTotal()} / {test.totalMarks}</span>
          </div>

          {modificationTypes.length > 0 && (
            <Form.Group className="mb-2">
              <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Modification Type</Form.Label>
              <Form.Select size="sm" value={modifyTypeId} onChange={(e) => setModifyTypeId(e.target.value)} disabled={saving}>
                <option value="">--Select--</option>
                {modificationTypes.map((mt: any) => (
                  <option key={mt.id} value={mt.id}>{mt.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Remarks</Form.Label>
            <Form.Control as="textarea" rows={2} size="sm" value={modifyRemarks} onChange={(e) => setModifyRemarks(e.target.value)} disabled={saving} placeholder="Reason for modification" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowModifyModal(false)} disabled={saving}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSaveModify} disabled={saving || (data?.modificationTypes?.length > 0 && !modifyTypeId)} style={{ backgroundColor: "var(--status-success)", border: "1px solid var(--status-success)" }}>
            {saving ? <><Spinner role="status" animation="border" size="sm" className="me-1" />Saving...</> : "Save & Recalculate"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MarksModerationPanel;
